import { computed, ref, type Ref } from 'vue'

/**
 * Delegates remote filter keyboard events to PrimeVue Select internals
 * (onFilterKeyDown / onFilterBlur). Required because GpxSelect uses a custom
 * header filter input instead of PrimeVue client-side filter.
 */
export type GpxSelectPrimeInstance = {
  $id?: string
  focusedOptionId?: string
  focusedOptionIndex?: number
  virtualScrollerDisabled?: boolean
  virtualScroller?: { scrollToIndex: (index: number) => void }
  onFilterKeyDown?: (event: KeyboardEvent) => void
  onFilterBlur?: () => void
}

export function useGpxSelectPrimeKeyboard(
  primeSelectRef: Ref<GpxSelectPrimeInstance | null>,
) {
  const keyboardSyncTick = ref(0)

  const filterAriaAttrs = computed(() => {
    void keyboardSyncTick.value
    const select = primeSelectRef.value
    if (!select?.$id) {
      return {
        ariaOwns: undefined as string | undefined,
        ariaActiveDescendant: undefined as string | undefined,
      }
    }
    return {
      ariaOwns: `${select.$id}_list`,
      ariaActiveDescendant: select.focusedOptionId,
    }
  })

  function delegateFilterKeyDown(event: KeyboardEvent) {
    primeSelectRef.value?.onFilterKeyDown?.(event)
    keyboardSyncTick.value += 1
  }

  function delegateFilterBlur() {
    primeSelectRef.value?.onFilterBlur?.()
    keyboardSyncTick.value += 1
  }

  function resetFocusedOptionOnFilterInput() {
    const select = primeSelectRef.value
    if (!select) {
      return
    }
    select.focusedOptionIndex = -1
    if (!select.virtualScrollerDisabled && select.virtualScroller) {
      select.virtualScroller.scrollToIndex(0)
    }
    keyboardSyncTick.value += 1
  }

  return {
    filterAriaAttrs,
    delegateFilterKeyDown,
    delegateFilterBlur,
    resetFocusedOptionOnFilterInput,
  }
}
